'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { fetchTreatmentsByGuid, type HpTreatmentDto, isControlTreatment } from './hotpadsExperimentClient';
import { getGuidFromBrowser } from './guidCookie';
import { getAllOverrides } from './overrides';
import { EXPERIMENT_OVERRIDES_CHANGED_EVENT } from './queryParams';
import type { ExperimentName } from './registry';
import type { TreatmentResult } from './useTreatment';

/**
 * Batch hook: read multiple treatments in a single backend round-trip.
 *
 * `useTreatment` fans out one POST per call — fine for a page that consults
 * one experiment, wasteful when a route renders 3+ experiments because the
 * backend already accepts an `experiments: [...]` list. Call `useTreatments`
 * once at the top of the route with all the names you need and pass the
 * resulting map down (or read individual entries inline). Names are
 * deduplicated before the fetch so callers can pass overlapping lists.
 */
export function useTreatments(
  names: ReadonlyArray<ExperimentName>,
): Record<ExperimentName, TreatmentResult> {
  const uniqueNames = useMemo(() => Array.from(new Set(names)), [names]);

  const [overrides, setOverrides] = useState<Record<string, string>>(() =>
    typeof document !== 'undefined' ? getAllOverrides() : {},
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setOverrides(getAllOverrides());
    window.addEventListener(EXPERIMENT_OVERRIDES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(EXPERIMENT_OVERRIDES_CHANGED_EVENT, handler);
  }, []);

  const guid = typeof document !== 'undefined' ? getGuidFromBrowser() : undefined;
  const namesToFetch = uniqueNames.filter((n) => overrides[n] === undefined);

  const { data, isLoading } = useQuery<ReadonlyArray<HpTreatmentDto>, Error>({
    queryKey: ['experiment', 'treatments', namesToFetch, guid] as const,
    queryFn: async () => {
      if (!guid || namesToFetch.length === 0) return [];
      return fetchTreatmentsByGuid({ experiments: namesToFetch, userGuid: guid });
    },
    enabled: namesToFetch.length > 0,
    staleTime: Infinity,
    retry: 0,
    meta: {
      tags: ['experiment'],
      context: {
        api: 'hotpads-api',
        details: `fetch treatments [${namesToFetch.join(',')}]`,
      },
    },
  });

  const byName = useMemo(() => {
    const map = new Map<string, HpTreatmentDto>();
    // Lower-case keys so a backend normalization doesn't silently null-bucket
    // every user. Matched against `name.toLowerCase()` in the reduce below.
    for (const dto of data ?? []) map.set(dto.featureName.toLowerCase(), dto);
    return map;
  }, [data]);

  const result: Record<string, TreatmentResult> = {};
  for (const name of uniqueNames) {
    const override = overrides[name];
    if (override !== undefined) {
      result[name] = {
        treatment: override,
        isControl: isControlTreatment(override),
        isLoading: false,
        isOverridden: true,
      };
      continue;
    }
    const dto = byName.get((name as string).toLowerCase());
    result[name] = {
      treatment: dto?.treatment,
      isControl: isControlTreatment(dto?.treatment),
      isLoading: namesToFetch.length > 0 && isLoading,
      isOverridden: false,
    };
  }
  return result as Record<ExperimentName, TreatmentResult>;
}
